import { List, Map, Record } from "immutable";
import { OrgRecord, orgFromJSON } from "common/records/Org";
import { RootActions, RootState } from "common/reducers";
import { Nullable } from "common/types";
import { currentOrgId, currentOrgPrefConfig } from "common/selectors/org.selectors";
import { reducer as imageReducer } from "common/reducers/image.reducer";
import { reducer as priceListReducer } from "common/reducers/priceList.reducer";
import { reducer as activatedPriceListReducer } from "common/reducers/activatedPriceList.reducer";
import { PriceListActions } from "common/reducers/priceList.actions";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { OrgOverviewRecord, orgOverviewFromJSON } from "common/records/OrgOverview";
import { document } from "common/selectors/document.selectors";
import { menuItemFromJSON } from "common/records/MenuItem";
import { ImageActions } from "common/reducers/image.actions";
import {
  ADD_COMMISSION_STEP,
  CONCAT,
  CONCAT_DOCUMENT_ORDER,
  EDIT_SLIDE_MODAL,
  FETCH_ORG,
  ON_DRAG_END,
  ON_JOB_MENU_DRAG_END,
  ON_JOB_MENU_DRAG_OVER,
  OrgActions,
  RECEIVE_ORG,
  RECEIVE_ORG_OVERVIEWS,
  REMOVE,
  REMOVE_COMMISSION_STEP,
  RESET_CURRENT_ORG,
  SET_CURRENT_ORG_ID,
  SET_ORG_ERRORS,
  SET_ORG_IMAGE,
  SET_ORG_LOADED,
  UPDATE,
  UPDATE_PREF_ACL,
  UPDATE_PREF_CONFIG,
  UPDATE_SETTINGS_ACL,
  ON_CONTRACT_LANGUAGE_DRAG_END,
  ADD_CONTRACT_LANGUAGE,
} from "common/reducers/org.actions";
import { commissionStepfromJSON } from "common/records/CommissionStep";
import { form } from "common/selectors/rsfForm.selectors";
import { ActivatedPriceListActions } from "common/reducers/activatedPriceList.actions";
import { contractLanguageFromJSON } from "common/records/ContractLanguage";
import {
  IPricePresentationSlideData,
  PricePresentationSlideRecord,
  pricePresentationSlideFromJSON,
} from "common/records/PricePresentationSlide";
import { reducer as jobResultingReducer } from "common/reducers/jobResulting.reducer";
import { JobResultingActions } from "common/reducers/jobResulting.actions";
import { IJobResultingConfigData } from "common/records/JobResultingConfig";

export const OrgStateRecord = Record({
  overviewsById: Map<number, OrgOverviewRecord>(),
  byId: Map<number, OrgRecord>(),
  lastSavedById: Map<number, OrgRecord>(),
  // Org ID of the current view, Job page = job.org_id, Admin page = org in current view, otherwise null
  currentOrgId: null as Nullable<number>,
});

export const initialState = OrgStateRecord();

export type OrgState = typeof initialState;

export const initialOrgRecord = Record({ orgs: initialState })();
export type OrgRootRecord = typeof initialOrgRecord;

const model = "orgs";
export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }
  switch (action.type) {
    case SET_CURRENT_ORG_ID:
      return state.setIn([model, "currentOrgId"], action.payload.id);

    case RECEIVE_ORG:
      const immutableOrg = orgFromJSON(action.payload);
      state = receive(state, model, immutableOrg) as RootState;
      if (action.payload.images?.length) {
        action.payload.images.forEach((image) => {
          state = imageReducer(state, ImageActions.receiveImage(image));
        });
      }
      if (action.payload.activated_price_list) {
        const { activated_price_list } = action.payload;
        const overviewPriceList = {
          ...activated_price_list,
          id: activated_price_list.price_list_id,
        };
        state = activatedPriceListReducer(
          state,
          ActivatedPriceListActions.receiveActivatedPriceList(activated_price_list),
        );
        state = priceListReducer(state, PriceListActions.receivePriceListOverview(overviewPriceList, true));
      }
      if (action.payload.logo) {
        state = imageReducer(state, ImageActions.receiveImage(action.payload.logo));
      }
      if (action.payload.default_cover_image) {
        state = imageReducer(state, ImageActions.receiveImage(action.payload.default_cover_image));
      }
      if (action.payload.custom_signature_email_image) {
        state = imageReducer(state, ImageActions.receiveImage(action.payload.custom_signature_email_image));
      }

      if (action.payload.preferences?.config?.job_resulting) {
        const jobResultingConfig = action.payload.preferences?.config?.job_resulting as IJobResultingConfigData;
        state = jobResultingReducer(state, JobResultingActions.receiveJobResultingConfig(jobResultingConfig));
      }

      return state;

    case UPDATE_PREF_CONFIG: {
      const { rootPath, name, value } = action.payload;
      return state.setIn(rootPath.concat(name.split(".")), value);
    }

    case UPDATE_SETTINGS_ACL: {
      const { rootPath, name, value } = action.payload;
      return state.setIn(rootPath.concat(name), value) as RootState;
    }

    case UPDATE_PREF_ACL: {
      const { name, value } = action.payload;
      const orgId = currentOrgId(state);

      return state.setIn([model, "byId", orgId, "preferences", "acl", name], value) as RootState;
    }

    case RECEIVE_ORG_OVERVIEWS:
      action.payload.forEach((org) => {
        state = state.setIn([model, "overviewsById", org.id], orgOverviewFromJSON(org));
        if (org.images?.length) {
          org.images.forEach((image) => {
            state = imageReducer(state, ImageActions.receiveImage(image));
          });
        }
        if (org.logo) {
          state = imageReducer(state, ImageActions.receiveImage(org.logo));
        }
        if (org.default_cover_image) {
          state = imageReducer(state, ImageActions.receiveImage(org.default_cover_image));
        }
        if (org.custom_signature_email_image) {
          state = imageReducer(state, ImageActions.receiveImage(org.custom_signature_email_image));
        }
      });
      return state;

    case FETCH_ORG:
      state = fetch(state, model, orgFromJSON({ id: action.payload }));
      return reducer(state, OrgActions.setOrgErrors(action.payload, []));

    case RESET_CURRENT_ORG:
      const orgId = currentOrgId(state);
      if (!orgId) return state;

      return reset(state, model, orgId) as RootState;

    case SET_ORG_LOADED:
      return loaded(state, model, action.payload.orgId) as RootState;

    case SET_ORG_ERRORS: {
      const orgId = action.payload.orgId;
      const errors = action.payload.errors;

      if (state.getIn([model, "byId", orgId])) {
        return state.setIn([model, "byId", orgId, "errors"], errors);
      }
      return state.setIn([model, "byId", orgId], orgFromJSON({ errors }));
    }

    case SET_ORG_IMAGE: {
      const { imageId, display } = action.payload;
      let imageType = "";
      if (!display) {
        return state;
      }

      switch (display) {
        case "do_not_display":
          imageType = "logo_id";
          break;
        case "contract":
          imageType = "default_cover_image_id";
          break;
        case "estimate":
          imageType = "custom_signature_email_image_id";
          break;
        default:
      }
      return state
        .setIn([model, "byId", currentOrgId(state), imageType], imageId)
        .setIn([model, "lastSavedById", currentOrgId(state), imageType], imageId);
    }

    case UPDATE: {
      const { orgPath, name, value } = action.payload;
      const rootPath = [model, "byId", currentOrgId(state)].concat(orgPath);
      return state.setIn(rootPath.concat(name.split(".")), value);
    }

    case REMOVE: {
      const { orgPath, name, value } = action.payload;
      const rootPath = [model, "byId", currentOrgId(state)].concat(orgPath);
      return state.updateIn(rootPath.concat(name.split(".")), (values: List<any>) => {
        const index = values.indexOf(value);
        if (index < 0) return values;
        return values.delete(index);
      });
    }

    case CONCAT: {
      const { orgPath, name, value } = action.payload;
      const rootPath = [model, "byId", currentOrgId(state)].concat(orgPath);
      return state.updateIn(rootPath.concat(name.split(".")), (list: List<any>) => (list || List()).concat(value));
    }

    case CONCAT_DOCUMENT_ORDER: {
      const { orgPath, name, ids } = action.payload;
      const rootPath = [model, "byId", currentOrgId(state)].concat(orgPath);

      return state.updateIn(rootPath.concat(name.split(".")), (list) => {
        ids.forEach((id) => {
          const doc = document(state, { documentId: id });
          if (doc) {
            list = list.push(
              Map({
                id: doc.id,
                doc_type: "org",
                file_name: doc.name,
                filter_states: [],
              }),
            );
          }
        });
        return list;
      });
    }

    case ON_DRAG_END: {
      const { orgPath, name, oldIndex, newIndex } = action.payload;
      const rootPath = [model, "byId", currentOrgId(state)].concat(orgPath);
      const list = state.getIn(rootPath.concat(name.split("."))) as List<any>;
      const newList = list.delete(oldIndex).insert(newIndex, list.get(oldIndex));
      return state.setIn(rootPath.concat(name.split(".")), newList);
    }

    case ON_JOB_MENU_DRAG_END: {
      const { orgPath, name, section, oldIndex, newIndex } = action.payload;
      const rootPath = [model, "byId", currentOrgId(state)].concat(orgPath);
      const path = rootPath.concat(name.split("."));

      // moving section
      if (section === -1) {
        const list = state.getIn(path) as List<any>;
        const item = list.get(oldIndex);
        const newList = list.delete(oldIndex).insert(newIndex, item);
        return state.setIn(path, newList);
      }

      // moving item
      const oldSectionList = state.getIn(path.concat(section, "menuItems")) as List<any>;
      const item = oldSectionList.getIn([oldIndex]);
      const oldList = oldSectionList.delete(oldIndex).insert(newIndex, item);
      state = state.setIn(path.concat(section, "menuItems"), oldList);
      return state;
    }

    case ON_JOB_MENU_DRAG_OVER: {
      const { orgPath, name, oldSectionIndex, oldIndex, newSectionIndex, newIndex, newMenuItem } = action.payload;
      const rootPath = [model, "byId", currentOrgId(state)].concat(orgPath);

      // new item being added
      if (oldSectionIndex === -1 && newSectionIndex !== -1) {
        const newSectionList = state.getIn(rootPath.concat(name.split("."), newSectionIndex, "menuItems")) as List<any>;
        const newList = newSectionList.insert(newIndex, menuItemFromJSON({ id: newMenuItem || "" }));
        state = state.setIn(rootPath.concat(name.split("."), newSectionIndex, "menuItems"), newList);
        return state;
      }

      // moving between sections
      const oldSectionList = state.getIn(rootPath.concat(name.split("."), oldSectionIndex, "menuItems")) as List<any>;
      const item = oldSectionList.getIn([oldIndex]);
      const oldList = oldSectionList.delete(oldIndex);
      state = state.setIn(rootPath.concat(name.split("."), oldSectionIndex, "menuItems"), oldList);

      const newSectionList = state.getIn(rootPath.concat(name.split("."), newSectionIndex, "menuItems")) as List<any>;
      const newList = newSectionList.insert(newIndex, item);
      state = state.setIn(rootPath.concat(name.split("."), newSectionIndex, "menuItems"), newList);
      return state;
    }

    case ADD_COMMISSION_STEP: {
      const { orgId } = action.payload;

      state = state.updateIn(
        [
          "orgs",
          "byId",
          orgId as number,
          "settings",
          "config",
          "estimator",
          "commission_calculator",
          "custom_config",
          "steps",
        ],
        (value) => {
          const newValue = (value || List()).push(commissionStepfromJSON({}));
          return newValue;
        },
      );

      return state;
    }

    case REMOVE_COMMISSION_STEP: {
      const { orgId, index } = action.payload;

      state = state.updateIn(
        [
          "orgs",
          "byId",
          orgId as number,
          "settings",
          "config",
          "estimator",
          "commission_calculator",
          "custom_config",
          "steps",
        ],
        (value) => {
          const newValue = (value || List()).delete(index);
          return newValue;
        },
      );

      return state;
    }

    case EDIT_SLIDE_MODAL: {
      const { slideIndex } = action.payload;
      const orgId = currentOrgId(state);
      const slideForm = form(state, { name: "slideForm" });
      const slideRecord = pricePresentationSlideFromJSON(slideForm?.toJS() as Partial<IPricePresentationSlideData>);

      let slides: List<PricePresentationSlideRecord> =
        currentOrgPrefConfig(state, { path: ["enhanced_price_presentation", "slides"] }) || List();
      if (slideIndex === "new") {
        slides = slides.push(slideRecord);
      } else {
        slides = slides.set(parseInt(slideIndex), slideRecord);
      }
      return state.setIn(
        ["orgs", "byId", orgId, "preferences", "config", "enhanced_price_presentation", "slides"],
        slides,
      );
    }

    case ON_CONTRACT_LANGUAGE_DRAG_END: {
      const { oldIndex, newIndex } = action.payload;
      const orgId = currentOrgId(state);

      const contractLanguages = state.getIn([model, "byId", orgId, "contract_languages"]) as List<any>;

      const item = contractLanguages.get(oldIndex);
      let newList = contractLanguages.delete(oldIndex).insert(newIndex, item);

      newList = newList.map((lang, idx) => lang.set("sort_order", idx)) as List<any>;

      return state.setIn([model, "byId", orgId, "contract_languages"], newList);
    }

    case ADD_CONTRACT_LANGUAGE: {
      const orgId = currentOrgId(state);

      const contractLanguages = state.getIn([model, "byId", orgId, "contract_languages"]) as List<any>;
      const newContractLanguage = contractLanguageFromJSON({});
      const newList = contractLanguages.push(newContractLanguage);

      return state.setIn([model, "byId", orgId, "contract_languages"], newList);
    }

    default:
      return state;
  }
};
